import { resolveActivityTypeConfig } from './activityTypeConfig.js';

const einheitsfuehrerUndAufwaertsRoles = new Set([
  'admin',
  'leiter',
  'lehrhöhenretter_ausbildung',
  'lehrhöhenretter_technik',
  'lehrhöhenretter_einsatztaktik',
  'lehrhöhenretter',
  'einheitsführer',
]);

const administrativeOnlyRoles = new Set(['admin', 'leiter']);
const srhtBasicCourseRoles = new Set(['admin', 'leiter', 'lehrhöhenretter_ausbildung']);
const hiddenLogbookSelectionRoles = new Set(['bewerber_srht_extern', 'beobachter']);
const applicantRole = 'bewerber_srht';
const equipmentCheckDayEntryRoles = new Set([
  'admin',
  'leiter',
  'einheitsführer',
  'geräteprüfer',
]);

export const logbookEntryWizardSchema = {
  steps: [
    {
      id: 'dateChoice',
      type: 'date-choice',
      question: '1. Wann hat die Übung/der Einsatz statt gefunden?',
      options: [
        { value: 'today', label: 'Heute' },
        { value: 'custom', label: 'Kalenderdatum auswählen' },
      ],
      dateLabel: 'Kalenderdatum',
    },
    {
      id: 'entryType',
      type: 'select',
      question: '2. Wähle die Art der Tätigkeit aus',
      label: 'Art der Tätigkeit',
      options: [],
    },
  ],
};

export function getTodayIsoDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getInitialWizardAnswers() {
  const today = getTodayIsoDate();
  const [year, month, day] = today.split('-');

  return {
    dateChoice: '',
    date: '',
    dateDay: day,
    dateMonth: month,
    dateYear: year,
    entryType: '',
    responsibleId: '',
    responsibleMode: 'internal',
    responsibleNameSnapshot: '',
    responsibleCompanySnapshot: '',
    responsibleIsLehrhSnapshot: false,
    entryVariant: '',
    timeFrom: '',
    timeTo: '',
    hours: '',
    pauseAnswer: '',
    serviceDurationHours: '',
    keywords: [],
    copyToOthers: '',
    copyUserIds: [],
    copyUserOverrides: {},
    pdfProtocolAnswer: '',
  };
}

export function getWizardStepOptions(step) {
  return step.options || [];
}

function normalizeRoles(roles = []) {
  if (!Array.isArray(roles)) {
    return [];
  }

  return roles
    .filter((role) => typeof role === 'string')
    .map((role) => role.trim())
    .filter(Boolean);
}

export function hasSrhtBasicCourseAccess(userRoles) {
  const roles = normalizeRoles(userRoles);
  return roles.some((role) => srhtBasicCourseRoles.has(role));
}

export function hasEquipmentCheckDayEntryAccess(userRoles) {
  const roles = normalizeRoles(userRoles);
  return roles.some((role) => equipmentCheckDayEntryRoles.has(role) || role.startsWith('lehrhöhenretter'));
}

export function getSelectedEntryType(answers) {
  return answers.entryType || '';
}

export function getSelectedDate(answers) {
  if (answers.dateChoice === 'today') {
    return getTodayIsoDate();
  }
  if (answers.dateChoice === 'custom') {
    return answers.date || '';
  }
  return '';
}

export function getResponsibleQuestion(entryType, entryTypeMeta = null) {
  const resolvedConfig = resolveActivityTypeConfig(entryTypeMeta || {}, entryType);
  if (resolvedConfig.responsibleQuestionText) {
    return resolvedConfig.responsibleQuestionText;
  }

  const configuredMode = resolvedConfig.responsibleQuestionMode;
  if (configuredMode === 'lehrgangsleiter') {
    return '3. Wer war Lehrgangsleiter des SRHT Grundlehrganges?';
  }

  if (configuredMode === 'geraetepruefer') {
    return '3. Wer war der verantwortliche Geräteprüfer?';
  }

  if (configuredMode === 'einheitsfuehrer') {
    return '3. Wer war der Verantwortliche Einheitsführer?';
  }

  return '3. Wer war der Verantwortliche Übungsleiter?';
}

export function getEventContext(entryType, entryTypeMeta = null) {
  const resolvedConfig = resolveActivityTypeConfig(entryTypeMeta || {}, entryType);
  if (resolvedConfig.eventContextMode === 'uebung') {
    return 'Übung';
  }
  if (resolvedConfig.eventContextMode === 'einsatz') {
    return 'Einsatz';
  }
  if (resolvedConfig.eventContextMode === 'geraetepruefung') {
    return 'Geräteprüfung';
  }
  return 'Veranstaltung';
}

export function getTimeQuestion(entryType, entryTypeMeta = null) {
  const resolvedConfig = resolveActivityTypeConfig(entryTypeMeta || {}, entryType);
  if (resolvedConfig.timeQuestionText) {
    return resolvedConfig.timeQuestionText;
  }

  const configuredEventContext = resolvedConfig.eventContextMode;
  if (configuredEventContext === 'geraetepruefung') {
    return '4. Wie war der exakte Zeitraum der Geräteprüfung?';
  }

  if (configuredEventContext === 'uebung') {
    return '4. Wie war der exakte Zeitraum der Übung?';
  }

  if (configuredEventContext === 'einsatz') {
    return '4. Wie war der exakte Zeitraum des Einsatzes?';
  }

  if (configuredEventContext === 'veranstaltung') {
    return '4. Wie war der exakte Zeitraum der Veranstaltung?';
  }

  const context = getEventContext(entryType, entryTypeMeta);
  return `4. Wie war der exakte Zeitraum der ${context}?`;
}

function splitName(fullName = '') {
  const normalized = String(fullName || '').trim();
  const parts = normalized.split(',');

  if (parts.length >= 2) {
    return {
      lastName: parts[0].trim().toLowerCase(),
      firstName: parts.slice(1).join(',').trim().toLowerCase(),
      fullName: normalized.toLowerCase(),
    };
  }

  const tokens = normalized.split(/\s+/).filter(Boolean);
  if (tokens.length >= 2) {
    return {
      lastName: tokens[tokens.length - 1].toLowerCase(),
      firstName: tokens.slice(0, -1).join(' ').toLowerCase(),
      fullName: normalized.toLowerCase(),
    };
  }

  return {
    lastName: normalized.toLowerCase(),
    firstName: '',
    fullName: normalized.toLowerCase(),
  };
}

function compareUsersByName(userA, userB) {
  const nameA = splitName(userA.full_name || userA.fullName || '');
  const nameB = splitName(userB.full_name || userB.fullName || '');

  const lastNameCompare = nameA.lastName.localeCompare(nameB.lastName, 'de');
  if (lastNameCompare !== 0) {
    return lastNameCompare;
  }

  const firstNameCompare = nameA.firstName.localeCompare(nameB.firstName, 'de');
  if (firstNameCompare !== 0) {
    return firstNameCompare;
  }

  return nameA.fullName.localeCompare(nameB.fullName, 'de');
}

function isEinheitsfuehrerOderAufwaerts(user) {
  const roles = Array.isArray(user.roles) ? user.roles : [];
  return roles.some((role) => einheitsfuehrerUndAufwaertsRoles.has(role));
}

function hasHiddenLogbookSelectionRole(user) {
  const roles = normalizeRoles(user?.roles);
  return roles.some((role) => hiddenLogbookSelectionRoles.has(role));
}

function isApplicantUser(user) {
  const roles = normalizeRoles(user?.roles);
  return roles.includes(applicantRole);
}

export function isExclusiveApplicantUser(user) {
  const roles = normalizeRoles(user?.roles);
  return roles.length === 1 && roles[0] === applicantRole;
}

export function isEligibleResponsibleUser(user) {
  const roles = normalizeRoles(user?.roles);
  if (roles.length === 0) {
    return true;
  }

  if (hasHiddenLogbookSelectionRole(user)) {
    return false;
  }

  // Exclude users whose full role set consists only of admin/leiter.
  return !roles.every((role) => administrativeOnlyRoles.has(role));
}

export function sortResponsibleUsers(users = []) {
  const visibleUsers = users.filter((user) => {
    const fullName = user.full_name || user.fullName;
    if (fullName === 'Administrator') {
      return false;
    }

    return isEligibleResponsibleUser(user);
  });

  const highPriority = visibleUsers
    .filter((user) => isEinheitsfuehrerOderAufwaerts(user))
    .sort(compareUsersByName);

  const lowerPriority = visibleUsers
    .filter((user) => !isEinheitsfuehrerOderAufwaerts(user) && !isApplicantUser(user))
    .sort(compareUsersByName);

  const applicantUsers = visibleUsers
    .filter((user) => !isEinheitsfuehrerOderAufwaerts(user) && isApplicantUser(user))
    .sort(compareUsersByName);

  return [...highPriority, ...lowerPriority, ...applicantUsers];
}
